/**
 * MetaAds — dashboard de métricas Meta Ads (Facebook/Instagram).
 *
 * Lê /api/meta-ads/{summary,metrics,breakdowns} (dados coletados pelo
 * collectors-deebs) filtrando por client (activeClient) + período global.
 * Módulo NOVO, isolado do Google. Conexão/connect vive em Perfil.
 */

import { useEffect, useState } from 'react'
import Layout from '../../components/Layout.jsx'
import KpiCard from '../../components/KpiCard.jsx'
import { DataTable } from '../../components/DataTable.jsx'
import DailyTrendChart from '../../components/DailyTrendChart.jsx'
import PageState from '../../components/PageState.jsx'
import MetaAccountFilter, { useMetaAccountFilter } from '../../components/MetaAccountFilter.jsx'
import './metaPages.css'
import { useApp } from '../../context/AppContext.jsx'
import { fmtBRL, fmtNum, fmtPct } from '../../lib/format.js'
import {
  getMetaAdsSummary, getMetaAdsMetrics, getMetaAdsBreakdowns,
} from '../../db/api.js'

const BREADCRUMB = [{ label: 'Marketing' }, { label: 'Meta Ads' }]

// Métricas selecionáveis no gráfico diário (as mais relevantes de mídia paga).
// `fmt` decide formatação de eixo/tooltip; `color` a cor da série.
const CHART_METRICS = [
  { key: 'spend', label: 'Investimento', fmt: 'currency', color: '#1877F2' },
  { key: 'impressions', label: 'Impressões', fmt: 'number', color: '#8b5cf6' },
  { key: 'clicks', label: 'Cliques', fmt: 'number', color: '#0ea5e9' },
  { key: 'reach', label: 'Alcance', fmt: 'number', color: '#10b981' },
  { key: 'ctr', label: 'CTR', fmt: 'percent', color: '#f59e0b' },
  { key: 'cpc', label: 'CPC', fmt: 'currency', color: '#ef4444' },
  { key: 'cpm', label: 'CPM', fmt: 'currency', color: '#ec4899' },
]

const CAMPAIGN_COLUMNS = [
  { key: 'name', label: 'Campanha' },
  { key: 'spend', label: 'Investimento', align: 'right', format: 'currency' },
  { key: 'impressions', label: 'Impressões', align: 'right', format: 'number' },
  { key: 'clicks', label: 'Cliques', align: 'right', format: 'number' },
  { key: 'ctr', label: 'CTR', align: 'right', format: 'percent' },
  { key: 'cpc', label: 'CPC', align: 'right', format: 'currency' },
  { key: 'cpm', label: 'CPM', align: 'right', format: 'currency' },
  { key: 'reach', label: 'Alcance', align: 'right', format: 'number' },
]

export default function MetaAds() {
  const { activeClient, period, toast } = useApp()
  const clientId = activeClient?.id ?? null
  const { start, end } = period || {}
  const { accounts, accountIds, setAccountIds } = useMetaAccountFilter()
  const accountKey = accountIds.join(',')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState({ totals: {}, series: [] })
  const [campaigns, setCampaigns] = useState([])
  const [breakdowns, setBreakdowns] = useState({})
  const [bdTab, setBdTab] = useState('demographics')

  useEffect(() => {
    if (!start || !end) return
    let alive = true
    ;(async () => {
      try {
        setLoading(true)
        setError('')
        const [sum, camps, demo, loc, dev, time] = await Promise.all([
          getMetaAdsSummary(start, end, accountIds),
          getMetaAdsMetrics('campaign', start, end, accountIds),
          getMetaAdsBreakdowns('demographics', start, end, accountIds),
          getMetaAdsBreakdowns('location', start, end, accountIds),
          getMetaAdsBreakdowns('devices', start, end, accountIds),
          getMetaAdsBreakdowns('time', start, end, accountIds),
        ])
        if (!alive) return
        setSummary(sum || { totals: {}, series: [] })
        setCampaigns(Array.isArray(camps) ? camps : [])
        setBreakdowns({ demographics: demo, location: loc, devices: dev, time })
      } catch (err) {
        if (alive) setError(err.message || 'Falha ao carregar métricas Meta.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [start, end, clientId, accountKey])

  const t = summary.totals || {}
  const hasData = (summary.series || []).length > 0 || campaigns.length > 0

  // Período sem dados → toast de 6s. Página segue renderizando estrutura vazia
  // (KPIs zerados, gráfico/tabelas vazias); o usuário troca o período sem reload.
  useEffect(() => {
    if (!loading && !error && !hasData) {
      toast('Sem dados Meta no período selecionado. Ajuste o período ou aguarde a próxima coleta.', 'info', 6000)
    }
  }, [loading, error, hasData, toast])

  const campaignRows = campaigns.map((c) => ({ id: c.id, name: c.name || c.id, ...c.metrics }))

  return (
    <Layout
      title="Meta Ads"
      breadcrumb={BREADCRUMB}
      showPeriod
      topbarRight={<MetaAccountFilter accounts={accounts} selected={accountIds} onChange={setAccountIds} />}
    >
      <PageState loading={loading} error={error} loadingText="Carregando métricas…">
        <div className="page-stack">
          <div className="kpi-grid kpi-grid-4">
            <KpiCard icon="currency" label="Investimento" value={fmtBRL(t.spend)} hint="Total investido no período" />
            <KpiCard icon="eye" label="Impressões" value={fmtNum(t.impressions)} hint="Vezes que os anúncios foram exibidos" />
            <KpiCard icon="target" label="Cliques" value={fmtNum(t.clicks)} hint="Cliques nos anúncios" />
            <KpiCard icon="users" label="Alcance" value={fmtNum(t.reach)} hint="Pessoas únicas alcançadas" />
            <KpiCard icon="percent" label="CTR" value={fmtPct(t.ctr)} hint="Cliques / impressões" />
            <KpiCard icon="currency" label="CPC" value={fmtBRL(t.cpc)} hint="Custo por clique" />
            <KpiCard icon="currency" label="CPM" value={fmtBRL(t.cpm)} hint="Custo por mil impressões" />
            <KpiCard icon="play_circle" label="ThruPlays" value={fmtNum(t.thruplays)} hint="Reproduções de vídeo completas" />
          </div>

          <DailyTrendChart series={summary.series || []} metrics={CHART_METRICS} />

          <div className="section-card meta-section">
            <div className="t-title meta-section-title">Campanhas</div>
            <DataTable columns={CAMPAIGN_COLUMNS} data={campaignRows} keyExtractor="id" hoverable striped />
          </div>

          <div className="section-card meta-section">
            <div className="meta-tabs">
              {[
                ['demographics', 'Demografia'],
                ['location', 'Localização'],
                ['devices', 'Dispositivos'],
                ['time', 'Horário'],
              ].map(([k, label]) => (
                <button
                  key={k}
                  className={`btn btn-xs ${bdTab === k ? 'btn-black' : 'btn-ghost'}`}
                  onClick={() => setBdTab(k)}
                >
                  {label}
                </button>
              ))}
            </div>
            <BreakdownTable kind={bdTab} rows={breakdowns[bdTab] || []} />
          </div>
        </div>
      </PageState>
    </Layout>
  )
}

// Colunas de dimensão por tipo de breakdown.
const BD_DIMS = {
  demographics: [['age', 'Idade'], ['gender', 'Gênero']],
  location: [['country', 'País'], ['region', 'Região']],
  devices: [['device_platform', 'Dispositivo'], ['publisher_platform', 'Plataforma']],
  time: [['hour', 'Hora']],
}

function BreakdownTable({ kind, rows }) {
  const dims = BD_DIMS[kind] || []
  const columns = [
    ...dims.map(([key, label]) => ({ key, label, render: (v) => v ?? '—' })),
    { key: 'spend', label: 'Investimento', align: 'right', format: 'currency' },
    { key: 'impressions', label: 'Impressões', align: 'right', format: 'number' },
    { key: 'clicks', label: 'Cliques', align: 'right', format: 'number' },
    { key: 'reach', label: 'Alcance', align: 'right', format: 'number' },
  ]
  return <DataTable columns={columns} data={rows} emptyText="Sem dados neste breakdown." hoverable striped />
}
